import _extends from "@babel/runtime/helpers/esm/extends";
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { StyledPhotoCarouselSwipeableContainer } from './PhotoCarouselSwipeableContainer.styles';

// If we apply these properties via styled components, we will be destroying
// and building a new DOM element for every tiny change in swipingOffset as the
// state changes. This leads to a choppy animation and poor UX. We can get
// around this by applying the css to the element via the style property.
var getContainerStyle = function getContainerStyle(swipingOffset) {
  return swipingOffset ? {
    transform: "translate3d(" + swipingOffset + "%, 0,0)"
  } : {};
};

/**
 * An HOC that uses react-swipeable to make its children swipeable
 * using either touch or a mouse.
 *
 * @since 8.66.0
 */

export var PhotoCarouselSwipeableContainer = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var onSwiping = _ref.onSwiping,
    onSwipedLeft = _ref.onSwipedLeft,
    onSwipedRight = _ref.onSwipedRight,
    onSwipedUp = _ref.onSwipedUp,
    onSwipedDown = _ref.onSwipedDown,
    shouldAnimateTranslations = _ref.shouldAnimateTranslations,
    swipingOffset = _ref.swipingOffset,
    children = _ref.children;
  var handlers = useSwipeable({
    delta: 1,
    onSwipedLeft: onSwipedLeft,
    onSwipedRight: onSwipedRight,
    onSwipedUp: onSwipedUp,
    onSwipedDown: onSwipedDown,
    onSwiping: onSwiping
  });
  return /*#__PURE__*/React.createElement(StyledPhotoCarouselSwipeableContainer, {
    ref: forwardedRef,
    shouldAnimateTranslations: shouldAnimateTranslations,
    style: getContainerStyle(swipingOffset)
  }, /*#__PURE__*/React.createElement("div", _extends({
    id: "swipeable"
  }, handlers), children));
});
PhotoCarouselSwipeableContainer.displayName = 'PhotoCarouselSwipeableContainer';
PhotoCarouselSwipeableContainer.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * An array of `PhotoCarouselSlide` components provided by `PhotoCarousel`.
   */
  children: PropTypes.node.isRequired,
  /**
   * Callback invoked after a down swipe.
   */
  onSwipedDown: PropTypes.func.isRequired,
  /**
   * Callback invoked after a left swipe.
   */
  onSwipedLeft: PropTypes.func.isRequired,
  /**
   * Callback invoked after a right swipe.
   */
  onSwipedRight: PropTypes.func.isRequired,
  /**
   * Callback invoked after an up swipe.
   */
  onSwipedUp: PropTypes.func.isRequired,
  /**
   * Callback invoked during swiping.
   */
  onSwiping: PropTypes.func.isRequired,
  /**
   * If true, the sliding animation is enabled.
   */
  shouldAnimateTranslations: PropTypes.bool,
  /**
   * Provided by `PhotoCarousel`. Represents the percent of the Property Card's
   * width the current touch event has travelled.
   */
  swipingOffset: PropTypes.number
} : {};
PhotoCarouselSwipeableContainer.defaultProps = {
  shouldAnimateTranslations: true,
  swipingOffset: 0
};